import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Wrench, Receipt as ReceiptIcon, Users, UserSquare2, Search, BarChart3, Store, AlertTriangle, FileWarning, AlarmClock, Star, CalendarClock, MessageCircle, Megaphone, FileDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import PendingRemindersDialog, { shouldAutoShowReminders } from "@/components/PendingRemindersDialog";
import OverdueFollowUpsDialog from "@/components/OverdueFollowUpsDialog";
import AgingBucketsWidget from "@/components/AgingBucketsWidget";
import DocumentPendingDialog from "@/components/DocumentPendingDialog";
import UrgentDialog from "@/components/UrgentDialog";
import UrgentReasonDialog from "@/components/UrgentReasonDialog";
import ReadyBroadcastDialog from "@/components/ReadyBroadcastDialog";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";
import { formatBill } from "@/lib/format";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-md overflow-hidden bg-white border border-slate-200 flex shadow-sm" data-testid={testid}>
      <div className="w-16 shrink-0 flex items-center justify-center" style={{ background: color }}>
        <Icon className="h-7 w-7 text-white" strokeWidth={1.8} />
      </div>
      <div className="flex-1 px-4 py-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-display font-black text-3xl tracking-tight text-slate-900 font-mono-num mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user: currentUser } = useAuth();
  const isBranch = currentUser?.role === "branch";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderAuto, setReminderAuto] = useState(false);
  const [reminderMinDays, setReminderMinDays] = useState(15);
  const [pendingCount, setPendingCount] = useState(0);
  const [docPendingOpen, setDocPendingOpen] = useState(false);
  const [docPendingCount, setDocPendingCount] = useState(0);
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [urgentCount, setUrgentCount] = useState(0);
  const [urgentDlg, setUrgentDlg] = useState({ open: false, row: null });
  const [overdueFuOpen, setOverdueFuOpen] = useState(false);
  const [overdueFuCount, setOverdueFuCount] = useState(0);
  const [waRow, setWaRow] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [readyCount, setReadyCount] = useState(0);
  const canChangeStatus =
    currentUser?.role === "admin" ||
    currentUser?.role === "manager" ||
    currentUser?.role === "branch" ||
    !!currentUser?.permissions?.["Change Status"]?.view;
  const canOverrideStatus =
    currentUser?.role === "admin" ||
    currentUser?.role === "manager";
  const canSeeReminders = ["admin", "branch", "manager"].includes(currentUser?.role);
  const canFlagUrgent   =
    currentUser?.role === "admin" ||
    !!currentUser?.permissions?.["Urgent Work"]?.edit;
  const canSeeAging =
    currentUser?.role === "admin" ||
    !!currentUser?.permissions?.["Collection Health"]?.view;

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/dashboard/overview", { params: { branch_id: branchId } });
      setData(d);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isBranch) {
      api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    }
  }, [isBranch]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  // Fetch pending count + trigger auto-popup every 15 days for eligible roles
  useEffect(() => {
    if (!canSeeReminders || !currentUser?.id) return;
    let cancelled = false;
    api
      .get("/dashboard/pending-reminders", { params: { min_days: 15 } })
      .then((r) => {
        if (cancelled) return;
        const count = r.data?.count || 0;
        setPendingCount(count);
        const { shouldShow } = shouldAutoShowReminders(currentUser.id, currentUser.role);
        if (count > 0 && shouldShow) {
          setReminderAuto(true);
          setReminderOpen(true);
        }
      })
      .catch(() => {});
    // Document Pending count (branch-scoped)
    api.get("/dashboard/document-pending")
      .then((r) => { if (!cancelled) setDocPendingCount(r.data?.count || 0); })
      .catch(() => {});
    // Urgent count (branch-scoped)
    api.get("/dashboard/urgent")
      .then((r) => { if (!cancelled) setUrgentCount(r.data?.count || 0); })
      .catch(() => {});
    // Ready-for-Collection count (branch-scoped) for bulk WhatsApp broadcast
    api.get("/dashboard/ready-for-broadcast")
      .then((r) => { if (!cancelled) setReadyCount(r.data?.count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [currentUser?.id, branchId]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) { setResults(null); return; }
    const { data: d } = await api.get("/search/services", { params: { q } });
    setResults(d);
  };

  // Admin-only Excel download for the current search results.
  const isAdmin = currentUser?.role === "admin";
  const exportSearch = async () => {
    if (!results || results.length === 0) return;
    try {
      const rows = results.map((r) => ({
        doc_no: r.doc_no, application_id: r.application_id || "", doc_date: r.doc_date,
        type: r.type, customer_name: r.customer_name, mobile: r.mobile, service_name: r.service_name,
        agent_name: r.agent_name || "", paid_amount: r.paid_amount, balance: r.balance,
        operator: r.operator || "", supervisor: r.supervisor || "", status: r.status || "Pending",
      }));
      // Use browser-side CSV export so all currently visible results are captured (respecting the
      // exact search filter) — no extra backend round-trip.
      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(","),
        ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
      ].join("\n");
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dashboard-search-${q}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const lastUpdated = useMemo(() => {
    if (!data?.last_updated) return "";
    return new Date(data.last_updated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }, [data]);

  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Header actions */}
      <div className="flex items-center justify-between -mt-2 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {isBranch && (
            <div className="inline-flex items-center gap-2 bg-[color:var(--brand-navy)] text-white px-3 py-1.5 rounded-md text-xs font-semibold">
              <Store className="h-3.5 w-3.5" />
              Branch: {currentUser?.name}
            </div>
          )}
          {data?.scope === "personal" && (
            <div className="inline-flex items-center gap-2 bg-[color:var(--brand-teal)] text-white px-3 py-1.5 rounded-md text-xs font-semibold" data-testid="scope-personal-badge">
              My Work · {currentUser?.name}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!isBranch && branches.length > 0 && (
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="dash-branch-filter">
                <Store className="h-3.5 w-3.5 mr-1.5 text-slate-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">— All Branches —</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="text-[11px] text-slate-500 hidden sm:block">
            Last updated: <span className="font-mono-num text-slate-700">{lastUpdated || "—"}</span>
          </div>
          {canSeeReminders && (
            <Button
              onClick={() => setDocPendingOpen(true)}
              className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold relative"
              data-testid="open-document-pending-btn"
              title="Documents pending (work on hold with reason)"
            >
              <FileWarning className="h-3.5 w-3.5 mr-1.5" />
              Document Pending
              {docPendingCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-red-700 text-[10px] font-black" data-testid="document-pending-count">
                  {docPendingCount}
                </span>
              )}
            </Button>
          )}
          {canSeeReminders && (
            <Button
              onClick={() => setUrgentOpen(true)}
              className="h-8 bg-red-700 hover:bg-red-800 text-white text-xs font-semibold relative"
              data-testid="open-urgent-btn"
              title="Urgent applications flagged by customers"
            >
              <AlarmClock className="h-3.5 w-3.5 mr-1.5" />
              Urgent Work
              {urgentCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-red-700 text-[10px] font-black animate-pulse" data-testid="urgent-count">
                  {urgentCount}
                </span>
              )}
            </Button>
          )}
          {canSeeReminders && (
            <Button
              onClick={() => setOverdueFuOpen(true)}
              className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold relative"
              data-testid="open-overdue-followups-btn"
              title="Follow-ups whose promised date is 15+ days overdue"
            >
              <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
              Overdue Follow-Ups
              {overdueFuCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-red-700 text-[10px] font-black animate-pulse" data-testid="overdue-followups-count">
                  {overdueFuCount}
                </span>
              )}
            </Button>
          )}
          {canSeeReminders && (
            <Button
              onClick={() => setBroadcastOpen(true)}
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold relative"
              data-testid="open-ready-broadcast-btn"
              title="Bulk WhatsApp: notify all customers whose work is Completed but not yet Delivered"
            >
              <Megaphone className="h-3.5 w-3.5 mr-1.5" />
              WhatsApp Ready
              {readyCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-emerald-700 text-[10px] font-black" data-testid="ready-broadcast-count">
                  {readyCount}
                </span>
              )}
            </Button>
          )}
          <Button
            onClick={load}
            disabled={loading}
            className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold"
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh Stats
          </Button>
        </div>
      </div>

      {canSeeReminders && (
        <PendingRemindersDialog
          open={reminderOpen}
          onOpenChange={setReminderOpen}
          autoTriggered={reminderAuto}
          minDays={reminderMinDays}
        />
      )}
      {canSeeReminders && (
        <DocumentPendingDialog
          open={docPendingOpen}
          onOpenChange={setDocPendingOpen}
          onCountChange={setDocPendingCount}
        />
      )}
      {canSeeReminders && (
        <UrgentDialog
          open={urgentOpen}
          onOpenChange={setUrgentOpen}
          onCountChange={setUrgentCount}
        />
      )}
      {canSeeReminders && (
        <OverdueFollowUpsDialog
          open={overdueFuOpen}
          onOpenChange={setOverdueFuOpen}
          onCountChange={setOverdueFuCount}
        />
      )}
      {canSeeReminders && (
        <ReadyBroadcastDialog
          open={broadcastOpen}
          onOpenChange={(v) => {
            setBroadcastOpen(v);
            // Refresh the badge count when the dialog closes
            if (!v) {
              api.get("/dashboard/ready-for-broadcast")
                .then((r) => setReadyCount(r.data?.count || 0))
                .catch(() => {});
            }
          }}
        />
      )}

      <UrgentReasonDialog
        open={urgentDlg.open}
        onOpenChange={(v) => setUrgentDlg((s) => ({ ...s, open: v }))}
        row={urgentDlg.row}
        onConfirm={async (r, reason) => {
          await api.patch(`/receipts/${r.id}/urgent`, { is_urgent: true, urgent_reason: reason });
          setResults((rs) => rs.map((x) => x.id === r.id ? { ...x, is_urgent: true, urgent_reason: reason } : x));
          api.get("/dashboard/urgent").then((rr) => setUrgentCount(rr.data?.count || 0)).catch(() => {});
        }}
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Wrench} label="Service" value={data?.totals.service ?? 0} color="#2E77E6" testid="stat-service" />
        <StatCard icon={ReceiptIcon} label="Receipt" value={data?.totals.receipt ?? 0} color="#2AA36F" testid="stat-receipt" />
        <StatCard icon={Users} label="Customer" value={data?.totals.customer ?? 0} color="#1EA0D2" testid="stat-customer" />
        <StatCard icon={UserSquare2} label="Agent" value={data?.totals.agent ?? 0} color="#E8A93A" testid="stat-agent" />
        <StatCard icon={Wrench} label="Daily Service" value={data?.totals.daily_service ?? 0} color="#E8A93A" testid="stat-daily-service" />
        <StatCard icon={ReceiptIcon} label="Daily Receipt" value={data?.totals.daily_receipt ?? 0} color="#64748B" testid="stat-daily-receipt" />
      </div>

      {/* Aging Buckets — Collection Health */}
      {canSeeAging && (
        <AgingBucketsWidget
          onBucketClick={(minDays) => {
            setReminderAuto(false);
            setReminderMinDays(minDays);
            setReminderOpen(true);
          }}
        />
      )}

      {/* Search Service */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="search-service-card">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <div className="font-display font-bold text-slate-900 tracking-tight text-sm">Search Service</div>
        </div>
        <form onSubmit={runSearch} className="p-4 flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by application ID, service no, mobile no, customer name or agent name…"
            className="h-10 flex-1"
            data-testid="dashboard-search-input"
          />
          <Button type="submit" className="h-10 bg-[#2E77E6] hover:bg-[#2361C4] text-white font-semibold px-6" data-testid="dashboard-search-btn">
            <Search className="h-4 w-4 mr-1.5" /> Search
          </Button>
        </form>
        {results !== null && (
          <div className="border-t border-slate-200 overflow-x-auto">
            {results.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400">No matches for “{q}”</div>
            ) : (
              <>
                {isAdmin && (
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
                    <span className="text-slate-600 font-semibold">{results.length} match{results.length === 1 ? "" : "es"} for “{q}”</span>
                    <Button
                      onClick={exportSearch}
                      className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
                      data-testid="dashboard-search-export-btn"
                      title="Admin: Download these search results (CSV)"
                    >
                      <FileDown className="h-3.5 w-3.5 mr-1.5" /> Export CSV
                    </Button>
                  </div>
                )}
              <table className="w-full text-sm crm-table">
                <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-center font-semibold w-14" title="Urgent">
                      <AlarmClock className="h-3.5 w-3.5 inline text-red-600" />
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">Doc No</th>
                    <th className="px-3 py-2 text-left font-semibold">App ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                    <th className="px-3 py-2 text-left font-semibold">Customer</th>
                    <th className="px-3 py-2 text-left font-semibold">Mobile</th>
                    <th className="px-3 py-2 text-left font-semibold">Service</th>
                    <th className="px-3 py-2 text-left font-semibold">Agent</th>
                    <th className="px-3 py-2 text-right font-semibold">Paid</th>
                    <th className="px-3 py-2 text-right font-semibold">Balance</th>
                    <th className="px-3 py-2 text-left font-semibold">Assigned To</th>
                    <th className="px-3 py-2 text-left font-semibold">Supervisor</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-center font-semibold w-28">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {results.map((r) => (
                    <tr key={r.id} className={r.is_urgent ? "bg-red-50" : ""}>
                      <td className="px-3 py-2 text-center">
                        {canFlagUrgent ? (
                          <button
                            onClick={async () => {
                              if (r.is_urgent) {
                                try {
                                  await api.patch(`/receipts/${r.id}/urgent`, { is_urgent: false });
                                  setResults((rs) => rs.map((x) => x.id === r.id ? { ...x, is_urgent: false, urgent_reason: "" } : x));
                                  api.get("/dashboard/urgent").then((rr) => setUrgentCount(rr.data?.count || 0)).catch(() => {});
                                } catch { /* ignore */ }
                              } else {
                                setUrgentDlg({ open: true, row: r });
                              }
                            }}
                            data-testid={`toggle-urgent-${r.id}`}
                            title={r.is_urgent ? `Urgent: ${r.urgent_reason || "no reason"}` : "Mark as URGENT"}
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-full transition-colors ${
                              r.is_urgent ? "bg-red-600 text-white shadow-sm animate-pulse" : "bg-slate-100 text-slate-400 hover:bg-red-100 hover:text-red-600"
                            }`}
                          >
                            <AlarmClock className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          r.is_urgent ? (
                            <span title={r.urgent_reason || ""} className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-red-600 text-white">
                              <AlarmClock className="h-3.5 w-3.5" />
                            </span>
                          ) : <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono-num font-semibold">
                        {formatBill(r.doc_no)}
                        {r.is_urgent && r.urgent_reason && (
                          <div className="text-[10px] text-red-600 font-normal italic mt-0.5 line-clamp-2 max-w-[220px]" title={r.urgent_reason}>
                            Urgent: {r.urgent_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono-num text-slate-700" data-testid={`search-app-id-${r.id}`}>
                        {r.application_id || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono-num text-slate-600">{r.doc_date}</td>
                      <td className="px-3 py-2 text-[color:var(--brand-teal)] font-semibold">{r.type}</td>
                      <td className="px-3 py-2">{r.customer_name}</td>
                      <td className="px-3 py-2 font-mono-num text-slate-600">{r.mobile}</td>
                      <td className="px-3 py-2">{r.service_name}</td>
                      <td className="px-3 py-2 text-slate-600">{r.agent_name || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-num font-semibold text-emerald-700">{inr(r.paid_amount)}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-red-600">{inr(r.balance)}</td>
                      <td className="px-3 py-2 text-slate-700 font-semibold">{r.operator || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">{r.supervisor || "—"}</td>
                      <td className="px-3 py-2">
                        {(() => {
                          const badge = (
                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${
                              r.status === "Delivered" ? "bg-purple-50 text-purple-700 border-purple-200" :
                              r.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                              r.status === "In Progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                              "bg-amber-50 text-amber-700 border-amber-200"
                            }`}>{r.status || "Pending"}</span>
                          );
                          const isLockedForMe = r.status_locked_by_operator && !canOverrideStatus;
                          if (canChangeStatus && !isLockedForMe) {
                            return (
                              <Select
                                value={r.status || "Pending"}
                                onValueChange={async (v) => {
                                  try {
                                    const payload = { status: v };
                                    // App-ID gate: moving forward from Pending requires an application id
                                    if (["In Progress", "Completed", "Delivered"].includes(v) && !r.application_id) {
                                      // eslint-disable-next-line no-alert
                                      const app = window.prompt(`Application ID is required before marking ${r.customer_name || "this record"} as ${v}.\n\nEnter Application ID:`);
                                      if (!app || !app.trim()) return;
                                      payload.application_id = app.trim();
                                    }
                                    if (v === "Delivered" && !r.collector_name) {
                                      // eslint-disable-next-line no-alert
                                      const c = window.prompt(`Collector name is required to mark ${r.customer_name || "this record"} as Delivered:`);
                                      if (!c || !c.trim()) return;
                                      payload.collector_name = c.trim();
                                    }
                                    await api.patch(`/receipts/${r.id}/status`, payload);
                                    setResults((rs) => rs.map((x) => x.id === r.id ? { ...x, status: v, application_id: payload.application_id || x.application_id, collector_name: payload.collector_name || x.collector_name, status_locked_by_operator: true, status_changed_by: currentUser?.name || currentUser?.email } : x));
                                  } catch (err) {
                                    const msg = err?.response?.data?.detail || "Status update failed";
                                    // eslint-disable-next-line no-alert
                                    alert(msg);
                                  }
                                }}
                              >
                                <SelectTrigger className={`h-7 text-[10px] w-32 font-semibold uppercase tracking-wider ${
                                  r.status === "Delivered" ? "bg-purple-50 text-purple-700 border-purple-200" :
                                  r.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                  r.status === "In Progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                  "bg-amber-50 text-amber-700 border-amber-200"
                                }`} data-testid={`search-status-${r.id}`} title="Change status"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-white">
                                  {["Pending", "In Progress", "Completed", "Delivered"].map((s) => (
                                    // All four options are always visible so staff/branch can move forward
                                    // through the full workflow. Backend rejects any backward move for
                                    // non-admin/manager users with a clear 403 message.
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          }
                          return (
                            <div data-testid={`search-status-locked-${r.id}`}>
                              {badge}
                              {r.status_locked_by_operator && (
                                <div className="text-[9px] text-red-600 font-semibold uppercase mt-0.5" title={`Changed by ${r.status_changed_by || "operator"}`}>
                                  Locked
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {(() => {
                          if (!r.mobile) return <span className="text-slate-300 text-xs">—</span>;
                          const isReady = r.status === "Completed" || r.status === "Delivered";
                          return (
                            <button
                              onClick={() => setWaRow(r)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-white text-[10px] font-bold uppercase tracking-wide shadow-sm ${isReady ? "bg-[#25D366] hover:bg-[#1FB258] animate-pulse" : "bg-emerald-600 hover:bg-emerald-700"}`}
                              title={isReady ? "Notify customer: document is ready" : "Send WhatsApp message"}
                              data-testid={`search-wa-${r.id}`}
                            >
                              <MessageCircle className="h-3 w-3" />
                              {isReady ? "Ready" : "WA"}
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </div>
        )}
      </div>

      {/* View charts toggle */}
      <div>
        <Button
          onClick={() => setShowChart((s) => !s)}
          className="h-8 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white text-xs font-semibold"
          data-testid="toggle-charts-btn"
        >
          <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> {showChart ? "Hide All Service Charts" : "View All Service Charts"}
        </Button>
      </div>

      {/* 30-day chart */}
      {showChart && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="daily-receipts-chart">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="font-display font-bold text-slate-900 tracking-tight text-sm">Daily Receipts (Last 30 Days)</div>
            <button onClick={() => setShowChart(false)} className="text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
          </div>
          <div className="p-4 h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.series || []} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const dt = new Date(d);
                    return `${String(dt.getDate()).padStart(2, "0")}-${dt.toLocaleString(undefined, { month: "short" })}`;
                  }}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={54}
                />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip formatter={(v, name) => [inr(v), name]} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="rect" />
                <Bar dataKey="advance" stackId="a" fill="#2E77E6" name="Advance" />
                <Bar dataKey="receipt" stackId="a" fill="#2AA36F" name="Receipt" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Doc ${formatBill(waRow.doc_no)} · ${waRow.service_name || ""}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="WhatsApp Message"
        />
      )}
    </div>
  );
}
